"use client";

import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { cn } from "@/lib/utils";
import type { InitialPreferences } from "@/hooks/useSwipeRecommender";

const USER_STORAGE_KEY = "vault88-user";

type Step = "welcome" | "tutorial" | "preferences";
type TutorialSlide = 0 | 1 | 2;
type PreferenceScreen = 0 | 1 | 2;

type Props = {
  onComplete: (preferences: InitialPreferences) => void;
};

/* ------------------------------------------------------------------ */
/*  Icons (inline SVGs matching SwipeDeck style)                       */
/* ------------------------------------------------------------------ */

function HeartIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
    </svg>
  );
}

function XIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" aria-hidden="true">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function HeartPlusIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
      <line x1="12" y1="8" x2="12" y2="16" stroke="white" strokeWidth="2" strokeLinecap="round" />
      <line x1="8" y1="12" x2="16" y2="12" stroke="white" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/*  Preference options                                                 */
/* ------------------------------------------------------------------ */

const PIECE_OPTIONS = ["Rings", "Earrings", "Necklaces", "Bracelets"];
const METAL_OPTIONS = ["White Gold", "Rose Gold", "Yellow Gold"];
const STONE_OPTIONS = ["Diamonds", "Rubies", "Emeralds", "Sapphires", "Multicolor"];

/* ------------------------------------------------------------------ */
/*  Shared transition                                                  */
/* ------------------------------------------------------------------ */

const slideVariants = {
  enter: { opacity: 0, x: 60 },
  center: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -60 }
};

/* ------------------------------------------------------------------ */
/*  Pill toggle button                                                 */
/* ------------------------------------------------------------------ */

function Pill({
  label,
  selected,
  onToggle
}: {
  label: string;
  selected: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={cn(
        "rounded-full border px-5 py-2.5 text-sm font-semibold tracking-wide transition",
        selected
          ? "border-royal bg-royal text-white"
          : "border-slate-300 bg-white text-slate-700 hover:border-slate-500"
      )}
    >
      {label}
    </button>
  );
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export function SwipeOnboarding({ onComplete }: Props) {
  const [step, setStep] = useState<Step>("welcome");
  const [userName, setUserName] = useState<string | null>(null);
  const [tutorialSlide, setTutorialSlide] = useState<TutorialSlide>(0);
  const [prefScreen, setPrefScreen] = useState<PreferenceScreen>(0);

  // Preference selections
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [selectedMetals, setSelectedMetals] = useState<string[]>([]);
  const [selectedStones, setSelectedStones] = useState<string[]>([]);

  // Check localStorage for existing user
  useEffect(() => {
    try {
      const stored = window.localStorage.getItem(USER_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as { firstName?: string };
        if (parsed.firstName) {
          setUserName(parsed.firstName);
        }
      }
    } catch {
      // silent
    }
  }, []);

  /* ---- Helpers ---- */

  const toggleItem = (
    list: string[],
    setter: (v: string[]) => void,
    item: string
  ) => {
    setter(list.includes(item) ? list.filter((i) => i !== item) : [...list, item]);
  };

  const handleShowMeEverything = (
    allOptions: string[],
    selected: string[],
    setter: (v: string[]) => void
  ) => {
    if (selected.length === allOptions.length) {
      setter([]);
    } else {
      setter([...allOptions]);
    }
  };

  // Map display labels → category match values used by the recommender.
  // These are the same word-boundary strings used in categoryConfig.ts.
  const PIECE_LABEL_TO_MATCH: Record<string, string> = {
    Rings: "ring",
    Earrings: "earring",
    Necklaces: "necklace",
    Bracelets: "bracelet"
  };

  const STONE_LABEL_TO_MATCH: Record<string, string> = {
    Diamonds: "diamond",
    Rubies: "ruby",
    Emeralds: "emerald",
    Sapphires: "sapphire",
    Multicolor: "multicolor"
  };

  const buildPreferences = (): InitialPreferences => {
    const prefs: InitialPreferences = {};
    if (selectedTypes.length > 0 && selectedTypes.length < PIECE_OPTIONS.length) {
      prefs.types = selectedTypes.map((t) => PIECE_LABEL_TO_MATCH[t] ?? t.toLowerCase());
    }
    if (selectedMetals.length > 0 && selectedMetals.length < METAL_OPTIONS.length) {
      prefs.metals = selectedMetals.map((m) => m.toLowerCase());
    }
    if (selectedStones.length > 0 && selectedStones.length < STONE_OPTIONS.length) {
      prefs.stones = selectedStones.map((s) => STONE_LABEL_TO_MATCH[s] ?? s.toLowerCase());
    }
    return prefs;
  };

  const handlePreferenceNext = () => {
    if (prefScreen < 2) {
      setPrefScreen((prefScreen + 1) as PreferenceScreen);
    } else {
      onComplete(buildPreferences());
    }
  };

  /* ---- Renders ---- */

  return (
    <div className="flex justify-center px-4 py-8 md:py-10">
      <div className="w-full max-w-md">
        <AnimatePresence mode="wait" initial={false}>
          {/* ======================== WELCOME ======================== */}
          {step === "welcome" && (
            <motion.div
              key="welcome"
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.25, ease: "easeInOut" }}
              className="space-y-6 text-center"
            >
              <div className="space-y-3">
                <p className="text-xs uppercase tracking-[0.3em] text-slate-500">The Swipe Deck</p>
                <h2 className="text-3xl font-semibold text-slate-900 font-[var(--font-display)]">
                  {userName ? `Let\u2019s go, ${userName}!` : "Welcome to the Swipe Deck"}
                </h2>
                <p className="text-slate-600">
                  Discover pieces you love by swiping through our curated collection. We&apos;ll learn your taste as you go.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setStep("tutorial")}
                className="w-full rounded-sm bg-royal px-6 py-3 text-sm font-semibold uppercase tracking-[0.3em] text-white shadow-soft transition hover:-translate-y-0.5"
              >
                {userName ? "Let\u2019s go" : "Get started"}
              </button>
            </motion.div>
          )}

          {/* ======================== TUTORIAL ======================== */}
          {step === "tutorial" && (
            <motion.div
              key={`tutorial-${tutorialSlide}`}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.25, ease: "easeInOut" }}
              className="space-y-6 text-center"
            >
              {tutorialSlide === 0 && (
                <>
                  <div className="flex justify-center gap-6">
                    <div className="flex flex-col items-center gap-2">
                      <div className="flex h-16 w-16 items-center justify-center rounded-full border-4 border-[#4ade80]">
                        <HeartIcon className="h-8 w-8 text-[#4ade80]" />
                      </div>
                      <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Swipe right</span>
                    </div>
                    <div className="flex flex-col items-center gap-2">
                      <div className="flex h-16 w-16 items-center justify-center rounded-full border-4 border-red-400">
                        <XIcon className="h-8 w-8 text-red-400" />
                      </div>
                      <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Swipe left</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <h3 className="text-xl font-semibold text-slate-900">Like or Pass</h3>
                    <p className="text-slate-600">
                      Swipe right or tap <strong>Like</strong> to save a piece. Swipe left or tap <strong>Dislike</strong> to skip it.
                    </p>
                  </div>
                </>
              )}

              {tutorialSlide === 1 && (
                <>
                  <div className="flex justify-center">
                    <div className="flex flex-col items-center gap-2">
                      <div className="flex h-16 w-16 items-center justify-center rounded-full border-4 border-[#c4a668] bg-[#c4a668]/10">
                        <HeartPlusIcon className="h-8 w-8 text-[#c4a668]" />
                      </div>
                      <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Super like</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <h3 className="text-xl font-semibold text-slate-900">Super Like</h3>
                    <p className="text-slate-600">
                      Swipe up — or tap the <strong>gold heart</strong> — on any card to super like it.
                    </p>
                  </div>
                </>
              )}

              {tutorialSlide === 2 && (
                <>
                  <div className="flex justify-center">
                    <div className="flex flex-col items-center gap-2">
                      <div className="flex h-16 w-16 items-center justify-center rounded-full border-4 border-slate-300 bg-slate-50">
                        <HeartIcon className="h-8 w-8 text-slate-700" />
                      </div>
                      <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Your likes</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <h3 className="text-xl font-semibold text-slate-900">Find Your Likes</h3>
                    <p className="text-slate-600">
                      Tap the heart icon in the header to see all the pieces you&apos;ve liked. Your favorites are always just a tap away.
                    </p>
                  </div>
                </>
              )}

              {/* Tutorial dots */}
              <div className="flex justify-center gap-2">
                {[0, 1, 2].map((i) => (
                  <div
                    key={i}
                    className={cn(
                      "h-2 w-2 rounded-full transition-colors",
                      i === tutorialSlide ? "bg-royal" : "bg-slate-300"
                    )}
                  />
                ))}
              </div>

              <button
                type="button"
                onClick={() => {
                  if (tutorialSlide < 2) {
                    setTutorialSlide((tutorialSlide + 1) as TutorialSlide);
                  } else {
                    setStep("preferences");
                  }
                }}
                className="w-full rounded-sm bg-royal px-6 py-3 text-sm font-semibold uppercase tracking-[0.3em] text-white shadow-soft transition hover:-translate-y-0.5"
              >
                {tutorialSlide < 2 ? "Next" : "Start swiping"}
              </button>
            </motion.div>
          )}

          {/* ===================== PREFERENCES ====================== */}
          {step === "preferences" && (
            <motion.div
              key={`pref-${prefScreen}`}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.25, ease: "easeInOut" }}
              className="space-y-6 text-center"
            >
              {prefScreen === 0 && (
                <>
                  <div className="space-y-2">
                    <p className="text-xs uppercase tracking-[0.3em] text-slate-500">1 of 3</p>
                    <h3 className="text-xl font-semibold text-slate-900">What types of pieces?</h3>
                    <p className="text-sm text-slate-500">Select all that interest you</p>
                  </div>
                  <div className="flex flex-wrap justify-center gap-3">
                    {PIECE_OPTIONS.map((opt) => (
                      <Pill
                        key={opt}
                        label={opt}
                        selected={selectedTypes.includes(opt)}
                        onToggle={() => toggleItem(selectedTypes, setSelectedTypes, opt)}
                      />
                    ))}
                    <Pill
                      label="Show Me Everything"
                      selected={selectedTypes.length === PIECE_OPTIONS.length}
                      onToggle={() => handleShowMeEverything(PIECE_OPTIONS, selectedTypes, setSelectedTypes)}
                    />
                  </div>
                </>
              )}

              {prefScreen === 1 && (
                <>
                  <div className="space-y-2">
                    <p className="text-xs uppercase tracking-[0.3em] text-slate-500">2 of 3</p>
                    <h3 className="text-xl font-semibold text-slate-900">What color metal?</h3>
                    <p className="text-sm text-slate-500">Select all that interest you</p>
                  </div>
                  <div className="flex flex-wrap justify-center gap-3">
                    {METAL_OPTIONS.map((opt) => (
                      <Pill
                        key={opt}
                        label={opt}
                        selected={selectedMetals.includes(opt)}
                        onToggle={() => toggleItem(selectedMetals, setSelectedMetals, opt)}
                      />
                    ))}
                    <Pill
                      label="Show Me Everything"
                      selected={selectedMetals.length === METAL_OPTIONS.length}
                      onToggle={() => handleShowMeEverything(METAL_OPTIONS, selectedMetals, setSelectedMetals)}
                    />
                  </div>
                </>
              )}

              {prefScreen === 2 && (
                <>
                  <div className="space-y-2">
                    <p className="text-xs uppercase tracking-[0.3em] text-slate-500">3 of 3</p>
                    <h3 className="text-xl font-semibold text-slate-900">What kinds of stones?</h3>
                    <p className="text-sm text-slate-500">Select all that interest you</p>
                  </div>
                  <div className="flex flex-wrap justify-center gap-3">
                    {STONE_OPTIONS.map((opt) => (
                      <Pill
                        key={opt}
                        label={opt}
                        selected={selectedStones.includes(opt)}
                        onToggle={() => toggleItem(selectedStones, setSelectedStones, opt)}
                      />
                    ))}
                    <Pill
                      label="Show Me Everything"
                      selected={selectedStones.length === STONE_OPTIONS.length}
                      onToggle={() => handleShowMeEverything(STONE_OPTIONS, selectedStones, setSelectedStones)}
                    />
                  </div>
                </>
              )}

              {/* Preference dots */}
              <div className="flex justify-center gap-2">
                {[0, 1, 2].map((i) => (
                  <div
                    key={i}
                    className={cn(
                      "h-2 w-2 rounded-full transition-colors",
                      i === prefScreen ? "bg-royal" : "bg-slate-300"
                    )}
                  />
                ))}
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={handlePreferenceNext}
                  className="flex-1 rounded-sm bg-royal px-6 py-3 text-sm font-semibold uppercase tracking-[0.3em] text-white shadow-soft transition hover:-translate-y-0.5"
                >
                  {prefScreen < 2 ? "Next" : "Start swiping"}
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Persistent skip — lets the user bail out of onboarding from any
            slide (welcome, tutorial, or preferences) and start swiping. */}
        <div className="mt-6 text-center">
          <button
            type="button"
            onClick={() => onComplete({})}
            className="text-xs text-slate-400 underline underline-offset-2 transition hover:text-slate-600"
          >
            Skip &amp; start swiping
          </button>
        </div>
      </div>
    </div>
  );
}
